import type { AuthUser } from '@financas/shared';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, refreshSession, setAccessToken, setSessionLostHandler } from '../lib/api';

/**
 * Estado de autenticacao da aba.
 *
 * Ao abrir a pagina nao ha access token — ele so existe em memoria. A sessao
 * e reconstruida pedindo um refresh: se o cookie httpOnly ainda for valido, a
 * pessoa entra direto; se nao, cai no login. E por isso que recarregar a
 * pagina nao expoe nada no armazenamento do navegador.
 */

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  signIn: (user: AuthUser, accessToken: string) => void;
  signOut: () => Promise<void>;
  reload: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const signIn = useCallback((nextUser: AuthUser, accessToken: string) => {
    setAccessToken(accessToken);
    setUser(nextUser);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const reload = useCallback(async () => {
    try {
      setUser(await api.me());
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let active = true;

    (async () => {
      const restored = await refreshSession();
      if (!active) return;

      if (restored) {
        try {
          setUser(await api.me());
        } catch {
          setUser(null);
        }
      }
      if (active) setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    // Disparado quando o refresh falha no meio de uma chamada qualquer.
    setSessionLostHandler(() => {
      setAccessToken(null);
      setUser(null);
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, signIn, signOut, reload }),
    [user, loading, signIn, signOut, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de AuthProvider.');
  return context;
}
